import { useCallback, useMemo, useState } from "react";

type RequiredSpec<T> =
  | Array<keyof T>
  | ((data: T) => Array<keyof T>);

type Options = {
  trimStrings?: boolean;
};

export function useRequiredFields<T extends Record<string, any>>(
  bean: T,
  required: RequiredSpec<T>,
  options: Options = { trimStrings: true }
) {
  const [missingFields, setMissingFields] = useState<Array<keyof T>>([]);

  const requiredFields = useMemo(() => {
    return typeof required === "function" ? required(bean) : required;
  }, [required, bean]);

  const validate = useCallback(() => {
    const missing = requiredFields.filter((field) => {
      const v = bean[field];

      if (v === undefined || v === null) return true;

      if (typeof v === "string") {
        return options.trimStrings ? v.trim() === "" : v === "";
      }

      if (typeof v === "number") return Number.isNaN(v);

      return false;
    });

    setMissingFields(missing);
    return missing.length;
  }, [bean, requiredFields, options.trimStrings]);

  return { missingFields, validate };
}